import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { CurrenciesActionTypes } from '../../../../../core/site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../../../core/site-context/store/actions/languages.action';
import { normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { concatLatestFrom } from '../../../../../core/util/rxjs/concat-latest-from';
import { CartAccountingCodesConnector } from '../../connector/cart-accounting-codes.connector';
import { CartActions } from '../actions';
import { CartAccountingCodesActionTypes, LoadCart, RemoveCartEntries } from '../actions/cart.actions';
import { StateWithCart } from '../cart-state';
import { CartSelectors } from '../selectors';

@Injectable()
export class CartAccountingCodesEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(CurrenciesActionTypes.CurrencyChange, LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout)
  );

  setAccountingCodeForAllEntries$: Observable<LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType<CartActions.SetAccountingCodeForAllEntries>(CartAccountingCodesActionTypes.SetAccountingCodeForAllEntries),
      map((action) => action.payload),
      concatLatestFrom((payload) => [
        this.store.select(CartSelectors.getCartFactory(payload.cartId)),
        this.store.pipe(select(CartSelectors.getActiveAccountingCode)),
      ]),
      switchMap(([payload, cart, accountingCode]) => {
        const entries = cart.entries
          .filter(
            (entry) =>
              entry.accountingCodes?.length === 0 ||
              entry.accountingCodes
                ?.map((accountingCodeAssignedForArticle) => accountingCodeAssignedForArticle.code)
                .includes(accountingCode.code)
          )
          .map((entry) => {
            return {
              accountingCode: accountingCode.code,
              entryNumber: entry.entryNumber,
            };
          });

        return this.connector.setAccountingCodes(payload.userId, payload.cartId, entries).pipe(
          map(() => {
            return new CartActions.LoadCart({
              cartType: payload.key,
              userId: payload.userId,
              cartId: payload.cartId,
            });
          })
        );
      })
    )
  );

  setAccountingCodeForAllEntriesAndRemoveIneligibleEntries$: Observable<RemoveCartEntries> = createEffect(() =>
    this.actions$.pipe(
      ofType<CartActions.SetAccountingCodeForAllEntriesAndRemoveIneligibleEntries>(
        CartAccountingCodesActionTypes.SetAccountingCodeForAllEntriesAndRemoveIneligibleEntries
      ),
      map((action) => action.payload),
      concatLatestFrom((payload) => [
        this.store.select(CartSelectors.getCartFactory(payload.cartId)),
        this.store.pipe(select(CartSelectors.getActiveAccountingCode)),
      ]),
      switchMap(([payload, cart, accountingCode]) => {
        const entriesToBeChanged = cart.entries
          .filter(
            (entry) =>
              entry.accountingCodes?.length === 0 ||
              entry.accountingCodes
                ?.map((accountingCodeAssignedForArticle) => accountingCodeAssignedForArticle.code)
                .includes(accountingCode.code)
          )
          .map((entry) => {
            return {
              accountingCode: accountingCode.code,
              entryNumber: entry.entryNumber,
            };
          });

        return this.connector.setAccountingCodes(payload.userId, payload.cartId, entriesToBeChanged).pipe(
          map(() => {
            const entriesForRemoval = cart.entries.filter(
              (entry) =>
                entry.accountingCodes?.length !== 0 &&
                !entry.accountingCodes
                  ?.map((accountingCodeAssignedForArticle) => accountingCodeAssignedForArticle.code)
                  .includes(accountingCode.code)
            );

            return new CartActions.RemoveCartEntries({
              cartType: payload.key,
              cartId: payload.cartId,
              userId: payload.userId,
              entries: entriesForRemoval,
            });
          })
        );
      })
    )
  );

  loadAccountingCodeList$: Observable<CartActions.LoadAccountingCodeListSuccess | CartActions.LoadAccountingCodeListFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartActions.CartAccountingCodesActionTypes.LoadAccountingCodeList),
        map((action: CartActions.LoadAccountingCodeList) => action.payload),
        switchMap((payload) =>
          this.connector.loadAccountingCodeList(payload).pipe(
            map((data) => new CartActions.LoadAccountingCodeListSuccess(data)),
            catchError((error) => of(new CartActions.LoadAccountingCodeListFail(normalizeHttpError(error))))
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  constructor(private actions$: Actions, private connector: CartAccountingCodesConnector, private store: Store<StateWithCart>) {}
}
